// PIÈCES TERRAIN d'une étude : la galerie (onglet Terrain), l'ajout, et la
// fiche d'une pièce (voir en grand, corriger la légende, supprimer).
import { useState } from 'react';
import { Button, EmptyState, ErrorBanner, Spinner } from '../components/ui';
import { Icon } from '../components/icons';
import { Drawer } from '../write/Drawer';
import { useCanWrite } from '../write/useCanWrite';
import { fieldFileUrl, useDeleteFieldItem, useFieldItems, useUpdateFieldItem, type FieldItem } from './api';
import { FieldCapture } from './FieldCapture';
import './capture.css';

const KIND_LABEL: Record<FieldItem['kind'], string> = { photo: 'Photo', video: 'Vidéo', audio: 'Voix', note: 'Note' };

function when(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

/** La vignette d'une pièce, dans la galerie. */
function Thumb({ item, onOpen }: { item: FieldItem; onOpen: () => void }) {
  const ready = item.file_status === 'ready';
  return (
    <li>
      <button type="button" className="field-thumb" onClick={onOpen} aria-label={`${KIND_LABEL[item.kind]} : ${item.caption ?? item.note_text ?? 'sans légende'}`}>
        <span className="field-thumb-media">
          {item.kind === 'photo' && ready ? (
            <img src={fieldFileUrl(item.id)} alt="" loading="lazy" />
          ) : item.kind === 'note' ? (
            <span className="field-thumb-note">{item.note_text}</span>
          ) : (
            <Icon name={item.kind === 'video' ? 'video' : item.kind === 'audio' ? 'mic' : 'camera'} size={28} />
          )}
          <span className="field-thumb-kind">{KIND_LABEL[item.kind]}</span>
        </span>
        <span className="field-thumb-caption">{item.caption ?? (item.kind === 'note' ? '' : 'Sans légende')}</span>
        <span className="field-thumb-meta">
          {item.file_status === 'uploading' ? 'Envoi inachevé' : when(item.taken_at)}
        </span>
      </button>
    </li>
  );
}

/** Une pièce en grand : le média, la légende modifiable, la suppression. */
function ItemViewer({ item, studyId, canWrite, onClose }: { item: FieldItem; studyId: string; canWrite: boolean; onClose: () => void }) {
  const update = useUpdateFieldItem(studyId);
  const remove = useDeleteFieldItem(studyId);
  const [caption, setCaption] = useState(item.caption ?? '');
  const [confirm, setConfirm] = useState(false);
  const src = fieldFileUrl(item.id);
  const ready = item.file_status === 'ready';

  return (
    <div className="field-viewer">
      {ready && item.kind === 'photo' ? <img className="field-full" src={src} alt={item.caption ?? 'Photo de terrain'} /> : null}
      {ready && item.kind === 'video' ? <video className="field-full" src={src} controls preload="metadata" /> : null}
      {ready && item.kind === 'audio' ? <audio className="recording-player" src={src} controls preload="metadata" /> : null}
      {item.kind === 'note' ? <p className="field-note-full">{item.note_text}</p> : null}
      {item.file_status === 'uploading' ? (
        <p className="card-note">L’envoi de ce fichier n’a pas abouti. Supprimez la pièce et ajoutez-la de nouveau.</p>
      ) : null}
      <p className="zy-field__aide">
        {[when(item.taken_at), item.created_by_name, item.interview_participant ? `Entretien : ${item.interview_participant}` : null]
          .filter(Boolean).join(' · ')}
      </p>
      {canWrite ? (
        <>
          <label className="zy-field">
            <span className="zy-field__label">Légende</span>
            <input className="zy-input" value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Ce que montre cette pièce" />
          </label>
          <div className="recorder-actions">
            <Button
              variant="ghost"
              disabled={caption === (item.caption ?? '')}
              loading={update.isPending}
              onClick={() => update.mutate({ id: item.id, caption })}
            >
              Enregistrer la légende
            </Button>
            {confirm ? (
              <>
                <Button variant="ghost" onClick={() => setConfirm(false)}>Garder</Button>
                <Button variant="ghost" loading={remove.isPending} onClick={() => remove.mutate(item.id, { onSuccess: onClose })}>
                  Supprimer définitivement
                </Button>
              </>
            ) : (
              <Button variant="ghost" onClick={() => setConfirm(true)}>Supprimer</Button>
            )}
          </div>
          {update.error ? <ErrorBanner>{update.error.message}</ErrorBanner> : null}
          {remove.error ? <ErrorBanner>{remove.error.message}</ErrorBanner> : null}
        </>
      ) : null}
    </div>
  );
}

export function FieldItemsSection({ projectId, studyId }: { projectId: string; studyId: string }) {
  const query = useFieldItems(studyId);
  const canWrite = useCanWrite(projectId);
  const [adding, setAdding] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const items = query.data?.items ?? [];
  const open = items.find((i) => i.id === openId) ?? null;

  return (
    <section className="test-block" id="etude-pieces">
      <div className="block-head">
        <h2 className="t-h3">Pièces terrain{items.length ? ` · ${items.length}` : ''}</h2>
        {canWrite ? (
          <Button variant="ghost" onClick={() => setAdding(true)}>
            <Icon name="plus" size={14} /> Ajouter
          </Button>
        ) : null}
      </div>
      <p className="card-note">Photos, vidéos, sons et notes pris sur place, rangés avec l’étude.</p>
      {query.isPending ? <Spinner label="Chargement des pièces" /> : null}
      {query.isError ? <ErrorBanner>{query.error.message}</ErrorBanner> : null}
      {query.isSuccess && items.length === 0 ? (
        <EmptyState
          title="Aucune pièce pour l’instant"
          description="Sur le terrain, ouvrez Zygosight sur votre téléphone et touchez « Capturer » : la photo arrive ici."
        />
      ) : null}
      {items.length ? (
        <ul className="field-grid">
          {items.map((i) => <Thumb key={i.id} item={i} onOpen={() => setOpenId(i.id)} />)}
        </ul>
      ) : null}

      <Drawer open={adding} title="Ajouter une pièce terrain" onClose={() => setAdding(false)}>
        <FieldCapture studyId={studyId} uploads={query.data?.uploads ?? true} onDone={() => setAdding(false)} />
      </Drawer>
      <Drawer open={open !== null} title={open ? `${KIND_LABEL[open.kind]}${open.caption ? ` · ${open.caption}` : ''}` : ''} onClose={() => setOpenId(null)}>
        {open ? <ItemViewer item={open} studyId={studyId} canWrite={canWrite} onClose={() => setOpenId(null)} /> : null}
      </Drawer>
    </section>
  );
}
